import { currencyGet, ApiShapeError } from '@/lib/task-api';

/**
 * ================== DUYURULAR (GET /v1/announcements) ======================
 *
 * `/yonetim/metrikler#duyuru` ekranindan gonderilen duyurularin OKUMA tarafi. Uc uzun sure
 * sunucuda hazir durup web'de hic kullanilmiyordu: platform yoneticisi duyuru gonderiyor, ama
 * duyuruyu yalnizca mobil bildirim olarak alanlar goruyordu — web'e giren kullanici icin
 * gonderilmemis sayiliyordu.
 *
 * KAPSAM SUNUCUDA, SORGUDA DEGIL: SQL `audience = 'all' OR audience = <cagiranin user_type>`
 * ve `target_user_id IS NULL OR = <cagiran>` kosullarini kendisi koyuyor (admin-scripts.js
 * readAnnouncementsScript). Istemci bir kitle parametresi GONDERMEZ; gonderseydi bir musteri
 * `audience=exchange` yazip buro duyurularini okuyabilirdi.
 *
 * Liste sozlesmesi: `{ items, page }`, `page.total` YOK — sayfalama `hasMore` uzerinden yurur.
 * Bu ekran ilk sayfayla yetiniyor (bkz. ANNOUNCEMENT_PAGE_SIZE gerekcesi).
 */

const ENDPOINT = '/v1/announcements';

/**
 * Tek sayfa, 20 kayit. Sonsuz kaydirma YOK: duyuru akisi bir arsiv degil, "yeni ne var"
 * sorusunun cevabi; 20 satirin otesi pratikte okunmuyor ve bir "daha fazla" dugmesi, ust
 * cubuktaki dar bir cekmecede gereksiz karmasiklik olurdu.
 */
export const ANNOUNCEMENT_PAGE_SIZE = 20;

export type Announcement = Readonly<{
  id: string;
  /** 'customer' | 'exchange' | 'all' — sunucu serbest metin dondurebilir. */
  audience: string;
  title: string;
  body: string;
  createdAt: string | null;
}>;

type RawEnvelope = Record<string, unknown> & { code?: string };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const asId = (value: unknown): string => {
  if (typeof value === 'string' && value !== '') return value;
  // pg int8'i string doner; kucuk kimliklerde number gorulebiliyor.
  if (typeof value === 'number') return String(value);
  throw new ApiShapeError(`${ENDPOINT}.id kimlik degil`);
};

export const fetchAnnouncements = async (): Promise<readonly Announcement[]> => {
  const { data } = await currencyGet<RawEnvelope>(ENDPOINT, {
    limit: ANNOUNCEMENT_PAGE_SIZE,
    offset: 0,
  });
  // `?? []` YOK: alan adi degisirse ekran hatasiz calisir ve "duyuru yok" der. Ne tsc ne test
  // bunu yakalar — beklenmeyen sekil FIRLATIR, "kayit yok" ile "yaniti anlayamadim" ayni
  // ekrani gostermemeli.
  if (!Array.isArray(data.items)) {
    throw new ApiShapeError(`${ENDPOINT}.items dizi degil`);
  }
  return data.items.map((raw, index) => {
    if (!isRecord(raw)) throw new ApiShapeError(`${ENDPOINT}.items[${index}] nesne degil`);
    return {
      id: asId(raw.id),
      audience: typeof raw.audience === 'string' ? raw.audience : 'all',
      title: typeof raw.title === 'string' ? raw.title : '',
      body: typeof raw.body === 'string' ? raw.body : '',
      createdAt: typeof raw.createdAt === 'string' ? raw.createdAt : null,
    };
  });
};

export const announcementKeys = {
  all: ['announcements'] as const,
};
